import {makeAutoObservable, runInAction} from "mobx"
import {merge} from "lodash-es"
import {Coordinate} from "ol/coordinate"
import {getCategories, TCategory, TFilter, TShortPoint} from "@ui/api/poi"
import poiService from "@ui/services/PoiService"
import {ActiveKey} from "@ui/components/services/poi/main"
import {Filter, IFilter, TListState} from "../common/filter"
import store from "../"

export enum PoiCategory {
  USER = "user"
}
export type TPoiCategory = string | typeof PoiCategory[keyof typeof PoiCategory]

type TState = {
  activeKey: ActiveKey
  list: TListState
}
const initState: TState = {activeKey: ActiveKey.CATEGORY, list: {}}
export interface IPoi {
  readonly filter: IFilter<TFilter>
  readonly categories: TCategory[]
  readonly points: TShortPoint[]
  readonly state: TState

  readonly selected: number | undefined

  loadCategories(): Promise<void>
  changeState(props: Partial<TState>): void
  appendSearch(points: TShortPoint[]): void
	clearListState(): void

  add(coords: Coordinate, point: TShortPoint): void
  update(id: number, props: {color?: number, title?: string, favorite?: boolean}): void
  select(id: number): void
  unselect(): void
  toggle(id: number, select: boolean): void
  purge(id: number): void
}

export class Poi implements IPoi {
  filter: IFilter<TFilter> = new Filter({name: ""})
  categories: TCategory[] = []
  points: TShortPoint[] = []
  state: TState = initState

  selected: number | undefined = undefined

  constructor() {
    makeAutoObservable(this)
  }

  async loadCategories() {
    const {debug} = store
    await getCategories()
      .then(categories => runInAction(() => this.categories = categories))
      .catch(error => debug.error(`Poi::getCategories::error: ${error.code}, ${error.message}`))
  }

  changeState(props: Partial<TState>) {
    this.state = merge(this.state, props)
  }

  appendSearch(points: TShortPoint[]): void {
		this.points = [...this.points, ...points]
	}

	clearListState(): void {
		this.points = []
    this.state = {...this.state, list: {}}
	}

  add(coords: Coordinate, point: TShortPoint) {
    poiService.addPoint(coords, point, this.toggle.bind(this))
  }

  update(id: number, props: {color?: number, title?: string, favorite?: boolean}) {
    this.points = this.points.map(point => point.id === id ? {...point, ...props} : point)
    if (props.color) {
      poiService.updatePointColor(id, props.color)
    }
  }

  select(id: number): void {
    this.selected = id
    poiService.selectPoint(id)
  }

  unselect(): void {
    this.selected = undefined
    poiService.unselectPoints()
  }

  toggle(id: number, select: boolean) {
    if (select) this.select(id)
    else this.unselect()
  }

  purge(id: number) {
    this.points = this.points.filter(point => point.id !== id)
    poiService.removePoint(id)
  }
}
